import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  Copy,
  ShieldCheck,
  Coins,
  Bitcoin,
  Banknote,
  Clock,
  QrCode,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { getOrder, markOrderPaid } from "../api";
import { formatAUD } from "../mock";
import { useToast } from "../hooks/use-toast";

const methodInfo = {
  xmr: { icon: Coins, name: "Monero", uri: (addr, amt) => `monero:${addr}?tx_amount=${amt}` },
  ltc: { icon: Bitcoin, name: "Litecoin", uri: (addr, amt) => `litecoin:${addr}?amount=${amt}` },
  bank: { icon: Banknote, name: "Bank transfer", uri: null },
};

const statusColor = (s) => {
  switch (s) {
    case "unpaid": return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "paid_reported": return "bg-blue-500/15 text-blue-300 border-blue-500/30";
    case "confirmed": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    default: return "bg-white/10 text-white/70 border-white/20";
  }
};
const statusLabel = (s) =>
  ({ unpaid: "Awaiting payment", paid_reported: "Payment reported — verifying", confirmed: "Payment confirmed" }[s] || s);

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [reporting, setReporting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    getOrder(orderNumber).then(setOrder).catch((e) => {
      setError(e?.response?.data?.detail || "Order not found");
    });
  }, [orderNumber]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: label || "Copied to clipboard" });
  };

  const reportPaid = async () => {
    if (reporting) return;
    setReporting(true);
    try {
      await markOrderPaid(orderNumber);
      const fresh = await getOrder(orderNumber);
      setOrder(fresh);
      toast({ title: "Thanks", description: "We'll verify on-chain and confirm shortly." });
    } catch (err) {
      toast({ title: "Couldn't update", description: String(err?.message || err) });
    } finally {
      setReporting(false);
    }
  };

  if (error) {
    return (
      <main className="min-h-screen bg-black text-white pt-24">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="card-glass rounded-3xl p-10 text-center">
            <div className="font-headline text-3xl">Order not found</div>
            <p className="mt-2 text-white/60">{error}</p>
            <Link to="/shop" className="btn-primary mt-6 inline-flex">Back to shop</Link>
          </div>
        </div>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="min-h-screen bg-black text-white pt-24">
        <div className="max-w-3xl mx-auto px-6 py-12 text-center text-white/50">Loading order…</div>
      </main>
    );
  }

  const info = methodInfo[order.payment_method];
  const Ico = info?.icon || Banknote;
  const uriString = info?.uri && order.crypto_address && order.crypto_amount
    ? info.uri(order.crypto_address, order.crypto_amount)
    : (order.crypto_address || "");

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-10 w-10 text-purple-400" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-purple-300">Order confirmed</div>
            <div className="font-headline text-4xl md:text-5xl font-bold text-white leading-[1]">Thank you.</div>
          </div>
        </div>
        <p className="mt-4 text-white/60 max-w-lg">
          {order.payment_method === "bank"
            ? "We'll be in touch from an encrypted address within 24 hours to arrange payment and confirm shipping."
            : "Send the exact crypto amount to the wallet below. We'll verify on-chain and confirm your order within 24 hours."}
        </p>

        {/* Reference card */}
        <div className="mt-8 card-glass rounded-3xl p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">Reference</div>
              <div className="font-display text-3xl font-bold text-white mt-1">{order.order_number}</div>
            </div>
            <span className={`px-3 py-1 text-[11px] uppercase tracking-[0.15em] rounded-full border ${statusColor(order.payment_status)}`}>
              {statusLabel(order.payment_status)}
            </span>
            <button onClick={() => copy(order.order_number, "Order reference copied.")} className="btn-ghost">
              <Copy className="h-4 w-4" /> Copy ref
            </button>
          </div>

          <div className="mt-6 border-t border-white/10 pt-6 space-y-3">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <div>
                  <div className="text-white/90">{it.product_name} · {it.variant}</div>
                  <div className="text-xs text-purple-300 uppercase tracking-[0.2em] mt-0.5">
                    {it.tier_name ? `${it.tier_name} tier · Qty ${it.qty}` : `Qty ${it.qty}`}
                  </div>
                </div>
                <div className="text-white font-display">{formatAUD(it.line_total)}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 border-t border-white/10 pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{formatAUD(order.subtotal)}</span></div>
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-purple-300"><span>Crypto discount ({order.discount_percent}%)</span><span>− {formatAUD(order.discount_amount)}</span></div>
            )}
            <div className="flex justify-between pt-2 border-t border-white/5">
              <span className="font-display">Total</span>
              <span className="font-display text-2xl font-bold text-white">{formatAUD(order.total)}</span>
            </div>
          </div>
        </div>

        {/* Crypto payment card */}
        {order.crypto_currency && (
          <div className="mt-6 card-glass rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <Ico className="h-5 w-5 text-purple-300" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">Pay in {info.name}</div>
                <div className="font-display text-2xl font-bold text-white">Send exact amount</div>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-[220px_1fr] gap-6 items-start">
              <div className="bg-white p-3 rounded-2xl w-fit">
                <QRCodeSVG value={uriString} size={196} level="M" includeMargin={false} />
              </div>
              <div className="space-y-4 min-w-0">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Amount</div>
                  <div className="mt-1 flex items-center justify-between gap-3 bg-black/40 border border-purple-500/20 rounded-xl px-4 py-3">
                    <span className="font-mono text-lg text-purple-300 select-all">
                      {order.crypto_amount} {order.crypto_currency}
                    </span>
                    <button onClick={() => copy(`${order.crypto_amount}`, "Amount copied.")} className="text-white/60 hover:text-white">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  {order.crypto_rate_aud && (
                    <div className="mt-1 text-[11px] text-white/40">
                      Rate locked at 1 {order.crypto_currency} = {formatAUD(Math.round(order.crypto_rate_aud))} at time of order.
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Wallet address</div>
                  <div className="mt-1 flex items-start justify-between gap-3 bg-black/40 border border-white/10 rounded-xl px-4 py-3">
                    <span className="font-mono text-xs text-white/90 break-all select-all">
                      {order.crypto_address}
                    </span>
                    <button onClick={() => copy(order.crypto_address, "Address copied.")} className="text-white/60 hover:text-white flex-shrink-0">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {order.payment_status === "unpaid" ? (
                  <button onClick={reportPaid} disabled={reporting} className="btn-primary w-full justify-center">
                    {reporting ? "Marking…" : (<>I've sent the payment <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                ) : order.payment_status === "paid_reported" ? (
                  <div className="flex items-center gap-2 text-sm text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3">
                    <Clock className="h-4 w-4" /> Verifying on-chain. We'll email your receipt within 24 hours.
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
                    <CheckCircle2 className="h-4 w-4" /> Payment confirmed. Build queued.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex items-start gap-2 text-[11px] text-white/40 leading-relaxed">
              <QrCode className="h-3.5 w-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
              <span>
                QR is generated in your browser and never sent to a third party. Send from any wallet.
                Amount and rate are locked at the moment of your order.
              </span>
            </div>
          </div>
        )}

        {order.payment_method === "bank" && (
          <div className="mt-6 card-glass rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <Banknote className="h-5 w-5 text-purple-300" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">Bank transfer</div>
                <div className="font-display text-2xl font-bold text-white">Payment instructions incoming</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-white/70">
              We'll email you PayID / BSB details from an encrypted address within an hour. Please
              use <span className="font-mono text-purple-300">{order.order_number}</span> as the transfer reference.
            </p>
          </div>
        )}

        <div className="mt-8 card-glass rounded-2xl p-5 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-purple-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-white/70">
            Every device ships in tamper-evident packaging with its GrapheneOS Auditor
            fingerprint printed on the receipt. Verify it in the Auditor app on first boot.
          </p>
        </div>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Link to="/shop" className="btn-primary">Order another <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/contact" className="btn-ghost">Contact us</Link>
        </div>
      </div>
    </main>
  );
};

export default OrderConfirmation;
